/**
 * Country picker + national-number input, bound to two form fields.
 *
 * The two are split on purpose. `name` keeps exactly what the agent typed, so
 * the field never rewrites itself out from under them; `countryName` keeps the
 * ISO code the number is read against. E.164 is derived at submit time via
 * `toE164`, which is the only shape the server is ever sent.
 *
 * The resolved number is echoed back under the input ("Will text +92 312
 * 3456789"). A number the agent can read back is the point of the control — a
 * Pakistani mobile typed as `0312…` is a valid string and a wrong number, and
 * that is the failure this replaces.
 */
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useFormContext, useWatch, type FieldPath, type FieldValues } from "react-hook-form";

import * as PopoverPrimitive from "@radix-ui/react-popover";

import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/utils/common.utils";
import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY,
  formatAsYouType,
  formatInternational,
  fromE164,
  isLikelyLandline,
  toE164,
} from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";

/**
 * The country list, rendered *without* a portal.
 *
 * `components/ui/popover.tsx` wraps its content in `PopoverPrimitive.Portal`,
 * which puts it at the end of `<body>`. That is wrong here specifically because
 * this field is used inside modals: `ParticipantDialog` is a modal Radix Dialog,
 * and a modal dialog sets `pointer-events: none` on `<body>` and traps focus
 * inside its own content. Portalled out, the list renders perfectly and is
 * completely inert — unclickable, and the search box cannot take a keystroke.
 *
 * Kept as a local composition rather than a `portal` prop on the shared
 * primitive, because `components/ui/` is generated by the shadcn CLI and
 * hand-edits there get overwritten on the next regeneration.
 */
const CountryPopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "start", sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      "z-50 w-72 rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none",
      className,
    )}
    {...props}
  />
));
CountryPopoverContent.displayName = "CountryPopoverContent";

interface PhoneFieldProps<TFieldValues extends FieldValues> {
  /** Field holding the typed national number. */
  name: FieldPath<TFieldValues>;
  /** Field holding the ISO 3166-1 alpha-2 country the number is read against. */
  countryName: FieldPath<TFieldValues>;
  label?: string;
  required?: boolean;
  testId?: string;
  /** The resolved-number echo below the input assumes the number will be
   *  texted ("Will text +1…"), which is only true where this field feeds an
   *  SMS invite. Callers that just collect a contact number (deal intake,
   *  profile forms) set this false so the copy doesn't imply a text is coming. */
  showResolved?: boolean;
}

export default function PhoneField<TFieldValues extends FieldValues = FieldValues>({
  name,
  countryName,
  label = "Mobile number",
  required = false,
  testId = "phone-field",
  showResolved = true,
}: PhoneFieldProps<TFieldValues>) {
  const { control, setValue, getValues, trigger } = useFormContext<TFieldValues>();
  const [open, setOpen] = useState(false);

  const country = (useWatch({ control, name: countryName }) as CountryCode) || DEFAULT_COUNTRY;
  const raw = (useWatch({ control, name: name }) as string) || "";

  // Tracked so the adoption effect below can leave a value the user is actively
  // typing completely alone.
  const [focused, setFocused] = useState(false);

  /**
   * Adopt an E.164 value the field was *seeded* with.
   *
   * Anything persisted is stored as `+923120287142`, so a form reseeded after a
   * save (or any page that loads a saved number) hands this field a string that
   * already carries its own country. Left as-is it renders as raw E.164 under
   * whichever country the consumer happened to default to — the picker claims
   * one country while the number states another.
   *
   * Doing it here rather than at each call site is deliberate: every portal
   * that ever shows a saved number gets the behaviour without having to know
   * about it, and there is no per-page step left to forget.
   *
   * Skipped while focused, so typing `+92…` by hand is never reformatted out
   * from under the cursor mid-keystroke; blur then tidies it.
   */
  useEffect(() => {
    if (focused || !raw.trim().startsWith("+")) return;
    const parsed = fromE164(raw);
    if (!parsed) return;  // half-typed — leave it be
    setValue(countryName, parsed.country as never, { shouldDirty: false });
    setValue(name, parsed.national as never, { shouldDirty: false });
  }, [raw, focused, name, countryName, setValue]);

  const selected = COUNTRY_OPTIONS.find((option) => option.value === country) ?? COUNTRY_OPTIONS[0];
  const e164 = useMemo(() => toE164(raw, country), [raw, country]);
  const landline = useMemo(() => isLikelyLandline(raw, country), [raw, country]);

  /** Re-reads the existing digits against the new country, then re-validates —
   *  switching US→PK has to move a number from valid to invalid immediately,
   *  not at the next keystroke. */
  const pickCountry = (next: CountryCode) => {
    setValue(countryName, next as never, { shouldDirty: true });
    const current = (getValues(name) as string) || "";
    setValue(name, formatAsYouType(current, "", next) as never, { shouldValidate: true, shouldDirty: true });
    setOpen(false);
    void trigger(name);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="space-y-1">
          <FormLabel>
            {label}
            {required && <span className="text-red-500" aria-hidden="true"> *</span>}
          </FormLabel>

          <div className="flex gap-2">
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="dashboardOutline"
                  role="combobox"
                  aria-expanded={open}
                  aria-label="Country calling code"
                  className="h-10 shrink-0 gap-1 !rounded-xl px-3 font-normal"
                  data-testid={`${testId}-country`}
                >
                  <span className="text-sm">{selected.short}</span>
                  <ChevronsUpDown className="size-3.5 opacity-50" />
                </Button>
              </PopoverTrigger>
              <CountryPopoverContent className="w-[280px]">
                <Command
                  // Search the whole label — "pak", "92" and "PK" should all
                  // find Pakistan, and the label carries name, code and digits.
                  filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
                >
                  <CommandInput placeholder="Search country or code…" data-testid={`${testId}-country-search`} />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {COUNTRY_OPTIONS.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={`${option.label} ${option.value}`}
                          onSelect={() => pickCountry(option.value)}
                          data-testid={`${testId}-country-${option.value}`}
                        >
                          <Check className={cn("mr-2 size-4", option.value === country ? "opacity-100" : "opacity-0")} />
                          <span className="truncate">{option.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </CountryPopoverContent>
            </Popover>

            <FormControl>
              <Input
                {...field}
                value={field.value ?? ""}
                onChange={(event) => field.onChange(formatAsYouType(event.target.value, raw, country))}
                onFocus={() => setFocused(true)}
                onBlur={() => { setFocused(false); field.onBlur(); }}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder={selected.value === DEFAULT_COUNTRY ? "(415) 555-0134" : "Mobile number"}
                aria-invalid={fieldState.invalid}
                data-testid={testId}
              />
            </FormControl>
          </div>

          {/* Only one line shows at a time, and the zod error outranks both:
              a number that failed validation has nothing useful to echo. */}
          {showResolved && !fieldState.error && e164 && (
            <FormDescription
              className={cn("mt-1", landline ? "text-dash-rust" : "text-dash-greenDeep")}
              data-testid={`${testId}-resolved`}
            >
              {landline
                ? `${formatInternational(e164)} looks like a landline — it may not receive texts.`
                : `Will text ${formatInternational(e164)}`}
            </FormDescription>
          )}
          <FormMessage className="mt-0.5" />
        </FormItem>
      )}
    />
  );
}
